import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, Save, FileText, Trash2, Download, Edit3, 
  Search, Plus, FolderOpen, Clock, CircleDot, Grid3X3,
  X, Copy, Eye, CheckCircle, Tag, Wrench, Cpu, Box,
  ChevronDown, ChevronUp, Settings2, Upload, ClipboardList
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sourceLabels = {
  'manual': 'Manual Entry',
  'ai-blueprint': 'AI Blueprint',
  'step': 'STEP Import',
  'stl': 'STL Import',
  'dxf': 'DXF Import'
};

const sourceColors = {
  'manual': 'bg-slate-500/20 text-slate-400',
  'ai-blueprint': 'bg-blue-500/20 text-blue-400',
  'step': 'bg-purple-500/20 text-purple-400',
  'stl': 'bg-cyan-500/20 text-cyan-400',
  'dxf': 'bg-amber-500/20 text-amber-400'
};

const SavedProgramsPage = () => {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [viewingProgram, setViewingProgram] = useState(null);
  const [expandedRecipe, setExpandedRecipe] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/programs`);
      setPrograms(response.data);
    } catch (error) {
      console.error('Error fetching programs:', error);
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteProgram = async (id) => {
    try {
      await axios.delete(`${API}/programs/${id}`);
      setPrograms(programs.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting program:', error);
      setPrograms(programs.filter(p => p.id !== id));
    }
    setShowDeleteConfirm(null);
  };

  const downloadProgram = (program) => {
    const blob = new Blob([program.gcode || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${program.name}.nc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const applyAsTemplate = (program) => {
    // Store recipe in sessionStorage so CADImportPage can load it
    sessionStorage.setItem('recipe_template', JSON.stringify({
      machine_setup: program.machine_setup,
      tools_used: program.tools_used,
      source: program.source
    }));
    navigate('/cad-import');
  };

  const filteredPrograms = programs.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = filter === 'all' || p.type === filter;
    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMaterialLabel = (key) => {
    const map = {
      'aluminum-6061': 'Aluminum 6061-T6',
      'steel-4140': 'Steel 4140',
      'steel-1018': 'Steel 1018',
      'stainless-304': 'Stainless 304',
      'stainless-316': 'Stainless 316',
      'titanium-6al4v': 'Titanium 6Al-4V',
      'brass-360': 'Brass C360',
      'cast-iron': 'Cast Iron',
      'plastic-delrin': 'Delrin/Acetal',
    };
    return map[key] || key || 'Unknown';
  };

  return (
    <div className="min-h-screen bg-industrial-bg pb-20">
      {/* Sub Header */}
      <div className="sticky top-14 z-20 bg-industrial-surface border-b border-industrial-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </button>
          
          <h2 className="font-heading font-bold text-industrial-safety flex items-center gap-2">
            <Save className="w-5 h-5" />
            SAVED PROGRAMS
          </h2>

          <button
            onClick={() => navigate('/cad-import')}
            className="p-2 rounded-lg hover:bg-industrial-surface-highlight transition-colors"
            data-testid="new-program-btn"
          >
            <Plus className="w-5 h-5 text-industrial-text-secondary" />
          </button>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-industrial-text-secondary" />
          <input
            type="text"
            placeholder="Search programs, tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-industrial-surface border border-industrial-border rounded-lg pl-10 pr-4 py-3 text-industrial-text placeholder:text-industrial-text-secondary"
            data-testid="search-input"
          />
        </div>

        <div className="flex gap-2">
          {['all', 'turning', 'milling'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-industrial-safety text-black'
                  : 'bg-industrial-surface border border-industrial-border text-industrial-text-secondary hover:border-industrial-safety'
              }`}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Programs List */}
      <div className="px-4 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-industrial-safety/30 border-t-industrial-safety rounded-full animate-spin" />
          </div>
        ) : filteredPrograms.length === 0 ? (
          <div className="bg-industrial-surface border border-industrial-border rounded-lg p-8 text-center">
            <FolderOpen className="w-12 h-12 mx-auto text-industrial-text-secondary mb-3" />
            <p className="text-industrial-text-secondary">
              {searchQuery ? 'No programs match your search' : 'No saved programs yet'}
            </p>
            <button
              onClick={() => navigate('/cad-import')}
              className="mt-4 px-6 py-2 bg-industrial-surface-highlight border border-industrial-border rounded-lg text-industrial-text hover:border-industrial-safety transition-colors"
              data-testid="create-program-btn"
            >
              Create New Program
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPrograms.map((program) => (
              <div
                key={program.id}
                className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden transition-all hover:border-industrial-safety/50"
                data-testid={`program-card-${program.id}`}
              >
                {/* Card Header */}
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        program.type === 'turning' 
                          ? 'bg-emerald-500/20' 
                          : 'bg-violet-500/20'
                      }`}>
                        {program.type === 'turning' ? (
                          <CircleDot className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <Grid3X3 className="w-5 h-5 text-violet-400" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-industrial-text truncate">{program.name}</h3>
                        <p className="text-xs text-industrial-text-secondary mt-0.5 truncate">
                          {program.description}
                        </p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <span className="text-xs px-2 py-0.5 bg-industrial-surface-highlight rounded text-industrial-text-secondary">
                            {program.controller?.toUpperCase()}
                          </span>
                          {program.source && (
                            <span className={`text-xs px-2 py-0.5 rounded ${sourceColors[program.source] || 'bg-gray-500/20 text-gray-400'}`}>
                              {sourceLabels[program.source] || program.source}
                            </span>
                          )}
                          <span className="text-xs text-industrial-text-secondary flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(program.updated_at)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <button
                        onClick={() => setViewingProgram(program)}
                        className="p-2 rounded hover:bg-industrial-surface-highlight transition-colors"
                        title="View G-Code"
                        data-testid={`view-btn-${program.id}`}
                      >
                        <Eye className="w-4 h-4 text-industrial-text-secondary" />
                      </button>
                      <button
                        onClick={() => downloadProgram(program)}
                        className="p-2 rounded hover:bg-industrial-surface-highlight transition-colors"
                        title="Download .nc file"
                        data-testid={`download-btn-${program.id}`}
                      >
                        <Download className="w-4 h-4 text-industrial-text-secondary" />
                      </button>
                      <button
                        onClick={() => {
                          sessionStorage.setItem('add_to_job_plan', JSON.stringify({
                            id: program.id,
                            name: program.name,
                            description: program.description,
                            tools_used: program.tools_used,
                          }));
                          navigate('/job-plans');
                        }}
                        className="p-2 rounded hover:bg-industrial-surface-highlight transition-colors"
                        title="Add to Job Plan"
                        data-testid={`add-to-plan-btn-${program.id}`}
                      >
                        <ClipboardList className="w-4 h-4 text-blue-400" />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(program.id)}
                        className="p-2 rounded hover:bg-red-500/20 transition-colors"
                        title="Delete"
                        data-testid={`delete-btn-${program.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>

                  {/* Tags */}
                  {program.tags?.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                      <Tag className="w-3 h-3 text-industrial-text-secondary" />
                      {program.tags.map((tag, i) => (
                        <span key={i} className="text-xs px-1.5 py-0.5 bg-industrial-bg border border-industrial-border rounded text-industrial-text-secondary">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Recipe expand toggle */}
                  {(program.machine_setup || program.tools_used) && (
                    <button
                      onClick={() => setExpandedRecipe(expandedRecipe === program.id ? null : program.id)}
                      className="flex items-center gap-1 mt-3 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                      data-testid={`expand-recipe-${program.id}`}
                    >
                      <Settings2 className="w-3 h-3" />
                      <span>Recipe Details</span>
                      {expandedRecipe === program.id ? (
                        <ChevronUp className="w-3 h-3" />
                      ) : (
                        <ChevronDown className="w-3 h-3" />
                      )}
                    </button>
                  )}
                </div>

                {/* Expanded Recipe Details */}
                {expandedRecipe === program.id && (
                  <div className="border-t border-industrial-border bg-industrial-bg/50 p-4 space-y-3">
                    {/* Machine Setup */}
                    {program.machine_setup && (
                      <div>
                        <div className="flex items-center gap-1.5 mb-2">
                          <Cpu className="w-3.5 h-3.5 text-industrial-safety" />
                          <span className="text-xs font-medium text-industrial-safety">Machine Setup</span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <div className="text-industrial-text-secondary">Machine Type</div>
                          <div className="text-industrial-text capitalize">{program.machine_setup.machine_type}</div>
                          <div className="text-industrial-text-secondary">Controller</div>
                          <div className="text-industrial-text uppercase">{program.machine_setup.controller}</div>
                          {program.machine_setup.workholding && (
                            <>
                              <div className="text-industrial-text-secondary">Workholding</div>
                              <div className="text-industrial-text">{program.machine_setup.workholding}</div>
                            </>
                          )}
                          {program.machine_setup.stock_material && (
                            <>
                              <div className="text-industrial-text-secondary">Material</div>
                              <div className="text-industrial-text">{getMaterialLabel(program.machine_setup.stock_material)}</div>
                            </>
                          )}
                          {program.machine_setup.stock_shape && (
                            <>
                              <div className="text-industrial-text-secondary">Stock Shape</div>
                              <div className="text-industrial-text capitalize">{program.machine_setup.stock_shape?.replace(/-/g, ' ')}</div>
                            </>
                          )}
                          {program.machine_setup.stock_diameter && (
                            <>
                              <div className="text-industrial-text-secondary">Stock Diameter</div>
                              <div className="text-industrial-text">{program.machine_setup.stock_diameter}"</div>
                            </>
                          )}
                          {program.machine_setup.stock_length && (
                            <>
                              <div className="text-industrial-text-secondary">Stock Length</div>
                              <div className="text-industrial-text">{program.machine_setup.stock_length}"</div>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Tools Used */}
                    {program.tools_used?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1.5 mb-2">
                          <Wrench className="w-3.5 h-3.5 text-blue-400" />
                          <span className="text-xs font-medium text-blue-400">Tools ({program.tools_used.length})</span>
                        </div>
                        <div className="space-y-1">
                          {program.tools_used.map((tool, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs bg-industrial-surface rounded px-2 py-1.5">
                              <span className="font-mono text-industrial-safety font-medium">T{String(tool.tool_number).padStart(2, '0')}</span>
                              <span className="text-industrial-text">{tool.description}</span>
                              {tool.insert_type && (
                                <span className="text-industrial-text-secondary ml-auto">{tool.insert_type}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Use as Template Button */}
                    <button
                      onClick={() => applyAsTemplate(program)}
                      className="w-full py-2.5 bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-400 text-sm font-medium flex items-center justify-center gap-2 hover:bg-blue-500/30 transition-colors"
                      data-testid={`use-template-${program.id}`}
                    >
                      <Upload className="w-4 h-4" />
                      Use as Template
                    </button>
                  </div>
                )}

                {/* Delete Confirmation */}
                {showDeleteConfirm === program.id && (
                  <div className="border-t border-industrial-border p-3 flex items-center justify-between bg-red-500/5">
                    <span className="text-sm text-red-400">Delete this program?</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowDeleteConfirm(null)}
                        className="px-3 py-1 text-sm bg-industrial-surface-highlight rounded text-industrial-text"
                        data-testid={`cancel-delete-${program.id}`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => deleteProgram(program.id)}
                        className="px-3 py-1 text-sm bg-red-500 rounded text-white"
                        data-testid={`confirm-delete-${program.id}`}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* G-Code Viewer Modal */}
      {viewingProgram && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-industrial-surface border border-industrial-border rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-industrial-border">
              <div>
                <h3 className="font-heading font-bold text-industrial-text">{viewingProgram.name}</h3>
                <p className="text-xs text-industrial-text-secondary mt-1">
                  {viewingProgram.type?.toUpperCase()} {viewingProgram.controller?.toUpperCase()}
                  {viewingProgram.source && ` | ${sourceLabels[viewingProgram.source] || viewingProgram.source}`}
                </p>
              </div>
              <button
                onClick={() => setViewingProgram(null)}
                className="p-2 hover:bg-industrial-surface-highlight rounded-lg transition-colors"
                data-testid="close-viewer-btn"
              >
                <X className="w-5 h-5 text-industrial-text-secondary" />
              </button>
            </div>

            {/* G-Code Content */}
            <div className="flex-1 overflow-auto p-4">
              <pre className="font-mono text-sm text-industrial-text bg-industrial-bg p-4 rounded-lg overflow-x-auto whitespace-pre-wrap">
                {viewingProgram.gcode || 'No G-code available'}
              </pre>
            </div>

            {/* Setup Sheet (if available) */}
            {viewingProgram.setup_sheet && (
              <div className="px-4 pb-2">
                <details className="bg-industrial-bg rounded-lg border border-industrial-border">
                  <summary className="px-3 py-2 text-sm font-medium text-industrial-safety cursor-pointer hover:bg-industrial-surface-highlight rounded-lg">
                    Setup Sheet
                  </summary>
                  <div className="px-3 pb-3 text-xs text-industrial-text-secondary">
                    <pre className="whitespace-pre-wrap">
                      {JSON.stringify(viewingProgram.setup_sheet, null, 2)}
                    </pre>
                  </div>
                </details>
              </div>
            )}

            {/* Modal Footer */}
            <div className="flex items-center gap-3 p-4 border-t border-industrial-border">
              <button
                onClick={() => copyToClipboard(viewingProgram.gcode || '')}
                className="flex-1 py-3 bg-industrial-surface-highlight border border-industrial-border rounded-lg font-medium text-industrial-text flex items-center justify-center gap-2 hover:border-industrial-safety transition-colors"
                data-testid="copy-gcode-btn"
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy G-Code
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  downloadProgram(viewingProgram);
                  setViewingProgram(null);
                }}
                className="flex-1 py-3 bg-industrial-safety text-black rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-industrial-safety/90 transition-colors"
                data-testid="download-gcode-btn"
              >
                <Download className="w-4 h-4" />
                Download .nc
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedProgramsPage;
